import React from "react";
import type { UseQueryResult } from "react-query";
import type { AxiosResponse, AxiosError } from "axios";
import memoize from "memoizee";
import debounce from "lodash/debounce";
import catalog from "@granular/fabric-catalog";
import { ensureError } from "../../../services/util";
import { getIsAxiosError } from "../../../services/granularAxios";

import {
  InvitesNetworkResponse,
  InvitesDataEnvelope,
} from "../../../models/invitations";
import { SupportedLocale } from "../../../models/locale";
import activities from "../../../redux/activities";
import { FetcherQueryKey, QueryFunction } from "../../../services/query";
import { getAuthToken } from "../../../services/granularAxios";
import { ensureValue } from "../../../services/util";
import invitesService from "../../../services/invites";
import {
  numberOfInvitesListsPossiblyActiveAtTheSameTime,
  refinedListOfPendingInvitations,
} from "../../../services/helpers/refinements";

import type { ResultOfUseSetInvitesGlobalActivityAndStatus } from "./invites.globalStatus";

export type InvitesFetcherParams = {
  userId: string | undefined;
  operationId: string | undefined;
  locale: SupportedLocale | undefined;
  catalogFlag: boolean | undefined;
};
export type InvitesQueryKey = FetcherQueryKey<InvitesFetcherParams>;

export const invitesQueryKeyPrefix = "[@fabric/core][invites]";

/**
 * Computes the invitesQueryKey from the given params.
 */
export const computeInvitesQueryKey = (
  params: InvitesFetcherParams,
): InvitesQueryKey => [invitesQueryKeyPrefix, params];

/**
 * Fetches the invites using the invitesService.
 */
export const invitesFetcher: QueryFunction<
  AxiosResponse<InvitesNetworkResponse> | null,
  InvitesFetcherParams
> = async (context) => {
  const catalogFlag = ensureValue(
    context.queryKey[1].catalogFlag,
    "catalogFlag",
  );
  const userId = ensureValue(context.queryKey[1].userId, "userId");
  const operationId = ensureValue(
    context.queryKey[1].operationId,
    "operationId",
  );
  const locale = ensureValue(context.queryKey[1].locale, "locale");
  if (catalogFlag) {
    const catalogConfig = {
      // IMPORTANT: We are not passing the authToken as part of the queryKey
      // because we don't want to invalidate our data on auth token change.
      // The auth token can and will change throughout the lifetime of the app,
      // but that should not invalidate our data.
      getAuthToken,
      getLocale: () => locale,
      getOperationId: () => operationId,
    };
    return catalog.invite
      .getInvitations({ operationId, catalogConfig })
      .catch((_e) => {
        const e = ensureError(_e);
        if (getIsAxiosError(e) && e.response && e.response.status === 403) {
          return null;
        }
        throw e;
      });
  }
  return invitesService.getInvitations({
    userId,
    operationId,
    locale,
    // IMPORTANT: We are not passing the authToken as part of the queryKey
    // because we don't want to invalidate our data on auth token change.
    // The auth token can and will change throughout the lifetime of the app,
    // but that should not invalidate our data.
    authToken: getAuthToken(),
  });
};

/**
 * Compute the invites data envelope.
 *
 * The memoization of this function may seem useless
 * but it is very important as it keeps the same object reference
 * between renders and across hook instances.
 *
 * This is only for use in computeDataEnvelopeFromResponse.
 * Otherwise, the memoization will not function properly.
 */
export const computeDataEnvelope = memoize(
  (
    pendingInvitations: InvitesDataEnvelope["pendingInvitations"],
  ): InvitesDataEnvelope => {
    return { pendingInvitations };
  },
  {
    max: numberOfInvitesListsPossiblyActiveAtTheSameTime,
    profileName: "[invites.query.helpers] computeDataEnvelope",
  },
);

/**
 * Compute the data envelope from the response.
 *
 * This is only for use in computeResultOfUseQuery.
 * Otherwise, the memoization will not function properly.
 *
 * There is no need for this function itself to be memoized
 * because its called functions are memoized and its result is memoized.
 */
export const computeDataEnvelopeFromResponse = (
  invitesAxiosResponse: AxiosResponse<InvitesNetworkResponse> | null,
): InvitesDataEnvelope => {
  // Compute the derived data for the data envelope.
  // (Don't worry. these compute functions are globally memoized.)
  const pendingInvitations = refinedListOfPendingInvitations(
    invitesAxiosResponse === null ? null : invitesAxiosResponse.data.message,
  );
  const dataEnvelope = computeDataEnvelope(pendingInvitations);
  return dataEnvelope;
};

/**
 * Compute the result of useQuery,
 * converting from the response data to the data envelope.
 *
 * This is only for use in useInvitesQuery.
 * Otherwise, the memoization will not function properly.
 */
export const computeResultOfUseQuery = (
  resultOfUseQuery: UseQueryResult<
    AxiosResponse<InvitesNetworkResponse> | null,
    AxiosError
  >,
): UseQueryResult<InvitesDataEnvelope, AxiosError> => {
  const dataEnvelope =
    resultOfUseQuery.data === undefined
      ? undefined
      : computeDataEnvelopeFromResponse(resultOfUseQuery.data);

  return {
    ...resultOfUseQuery,
    data: dataEnvelope,
  } as UseQueryResult<InvitesDataEnvelope, AxiosError>;
};

/**
 * Returns whether the current list of invites is equal to the previous.
 *
 * This uses etag comparison instead of a full deep comparison of the invites data.
 *
 * This is run once per network response as opposed to once per hook instance.
 *
 * This is only meant for use in useInvitesQuery.
 */
export const isDataEqual = (
  prevResponse: AxiosResponse<InvitesNetworkResponse> | null | undefined,
  response: AxiosResponse<InvitesNetworkResponse> | null,
): boolean => {
  // If we have no previous response, the data is not equal
  if (prevResponse === undefined) {
    return false;
  }
  // Otherwise, if the previous response and the current response
  // are both null, then the data is equal
  if (prevResponse === null && response === null) {
    return true;
  }
  // Otherwise, if either response is null, then the data is not equal
  if (prevResponse === null || response === null) {
    return false;
  }
  // Otherwise, the data equality is determined by the response etags
  return prevResponse.headers.etag === response.headers.etag;
};

/**
 * The returned value of this will contain
 * the React Query onSuccess function for useInvitesQuery
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useInvitesQuery.
 *
 * This is only meant for use in useInvitesQuery.
 */
export const onSuccess = debounce(
  ({
    setInvitesGlobalActivityAndStatus,
  }: {
    setInvitesGlobalActivityAndStatus: ResultOfUseSetInvitesGlobalActivityAndStatus;
  }) => {
    setInvitesGlobalActivityAndStatus({
      status: "success",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the React Query onError function for useInvitesQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useInvitesQuery.
 *
 * This is only meant for use in useInvitesQuery.
 */
export const onError = debounce(
  ({
    setInvitesGlobalActivityAndStatus,
  }: {
    setInvitesGlobalActivityAndStatus: ResultOfUseSetInvitesGlobalActivityAndStatus;
  }) => {
    setInvitesGlobalActivityAndStatus({
      status: "error",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * The returned value of this will contain
 * the function to call when loading in useInvitesQuery.
 *
 * This is debounced to run this function at most once per tick,
 * regardless of the number of instances of useInvitesQuery.
 *
 * This is only meant for use in useInvitesQuery.
 */
export const onLoading = debounce(
  ({
    setInvitesGlobalActivityAndStatus,
  }: {
    setInvitesGlobalActivityAndStatus: ResultOfUseSetInvitesGlobalActivityAndStatus;
  }) => {
    setInvitesGlobalActivityAndStatus({
      status: "loading",
      activity: activities.setCurrentOperation,
    });
  },
  0,
);

/**
 * This hook contains the logic to run in useInvitesQuery when loading.
 *
 * This hook is only meant for use in useInvitesQuery.
 */
export const useHandleLoadingStatus = ({
  resultOfUseQuery,
  setInvitesGlobalActivityAndStatus,
}: {
  resultOfUseQuery: UseQueryResult;
  setInvitesGlobalActivityAndStatus: ResultOfUseSetInvitesGlobalActivityAndStatus;
}): void => {
  const isStatusLoading = resultOfUseQuery.status === "loading";

  // Why a useEffect? So that this only sets the current operation sub data global
  // activity and status when these values change, as opposed to on
  // every render when we happen to be loading. This is a pain but is only
  // necessary to do while we preserve the behavior the deprecated data hooks.
  React.useEffect(() => {
    if (isStatusLoading) {
      onLoading({ setInvitesGlobalActivityAndStatus });
    }
  }, [isStatusLoading, setInvitesGlobalActivityAndStatus]);
};
